import {
  faCircle,
  faCircleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import { m } from "framer-motion";
import { useFormContext } from "react-hook-form";
import { twJoin, twMerge } from "tailwind-merge";

import { Icon } from "src/base-components/Icon";
import {
  StepId,
  useCurrentStep,
  useSteps,
} from "src/cases/templates/formSteps";
import { stepHasErrors } from "src/cases/templates/formUtils/errorNavigation";
import { useInvalidReferenceSteps } from "src/cases/templates/formUtils/InvalidReferenceContext";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";

export const CaseTemplateFormSidebar: React.FC = () => {
  const [currentStepId, setCurrentStep] = useCurrentStep();
  const steps = useSteps();
  const { formState } = useFormContext<CaseTemplateFormValues>();
  const invalidRefSteps = useInvalidReferenceSteps();

  return (
    <nav
      aria-label="Case template form steps"
      className="sticky top-0 w-full self-start rounded-lg border border-gray-200 bg-white px-3 py-4"
    >
      <ol className={twJoin("relative isolate flex flex-col gap-y-3")}>
        {/* Animated background */}
        <m.div
          animate={{
            y: steps.findIndex((step) => step.id === currentStepId) * 36, // 24px height + 12px gap
          }}
          className="absolute z-0 rounded-lg bg-gray-100"
          initial={false}
          style={{
            height: "24px",
            width: "100%",
            left: "0px",
            top: "0px",
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
          }}
        />
        {steps.map((step, index) => {
          const hasErrors =
            stepHasErrors(formState.errors, step.id as StepId) ||
            invalidRefSteps.has(step.id as StepId);

          return (
            <li key={step.id} className="relative" data-loc={`step-${step.id}`}>
              <StepButton
                active={currentStepId === step.id}
                completed={false}
                disabled={false}
                hasErrors={hasErrors}
                stepNumber={index + 1}
                totalSteps={steps.length}
                onClick={() => setCurrentStep(step.id)}
              >
                {step.title}
              </StepButton>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

const StepButton: React.FC<{
  active: boolean;
  disabled: boolean;
  completed: boolean;
  hasErrors: boolean;
  onClick: () => void;
  children: string;
  stepNumber: number;
  totalSteps: number;
}> = ({
  completed,
  active,
  disabled,
  hasErrors,
  onClick,
  children,
  stepNumber,
  totalSteps,
}) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  const getIcon = () => {
    if (hasErrors) return faCircleExclamation;
    if (completed) return faCircleCheck;
    return faCircle;
  };

  const getIconColor = () => {
    if (hasErrors) return "text-red-500";
    if (completed) return "text-green-600";
    return "text-gray-500";
  };

  return (
    <button
      aria-current={active ? "step" : undefined}
      aria-disabled={disabled}
      aria-label={`Step ${stepNumber} of ${totalSteps}: ${children}${completed ? " (completed)" : ""}${hasErrors ? " (has errors)" : ""}`}
      className={twMerge(
        "z-10 flex h-6 w-full items-center gap-x-2 rounded-lg px-2 transition-all duration-300",
        !disabled && !active && "cursor-pointer hover:bg-gray-50",
        disabled && "cursor-not-allowed [&>*]:opacity-50",
        "font-inter-medium-13px",
      )}
      disabled={disabled}
      type="button"
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <Icon
        aria-hidden="true"
        color={getIconColor()}
        icon={getIcon()}
        size="xs"
      />
      {children}
    </button>
  );
};
